import { addDays } from 'date-fns'
import { COMMON_PROVIDERS } from '@renderer/utils/email-utils'

/** The onboarding roles that are a professional function; Student, Other and the rest never see the invite. */
const PROFESSIONAL_ROLES = new Set([
  'HR/Recruiting',
  'Operations',
  'Marketing',
  'Sales',
  'Product Manager',
  'Design',
  'Engineering',
])

const DAY_MS = 24 * 60 * 60 * 1000
const MAYBE_LATER_BUSINESS_DAYS = 3

/** How the user has answered the invite, across launches and devices. */
export type SetupCallMemory = {
  /** When they first finished an audio note, staged or ran an action, or closed a meeting summary. */
  firstActionAt?: number
  /** Maybe later: the invite comes back at this time. */
  snoozedUntil?: number
  /** Don't show again. */
  dismissed?: boolean
  /** The moment the invite last opened on its own for; each opens once, not on every launch. */
  peeked?: SetupCallMoment
}

const earliest = (a?: number, b?: number) => (a && b ? Math.min(a, b) : (a ?? b))
const latest = (a?: number, b?: number) => (a && b ? Math.max(a, b) : (a ?? b))

/** The account's answers and this device's older ones together, each taken the way that shows the invite least. */
export function mergeMemory(account: SetupCallMemory, device: SetupCallMemory | undefined): SetupCallMemory {
  if (!device) return account
  return {
    firstActionAt: earliest(account.firstActionAt, device.firstActionAt),
    snoozedUntil: latest(account.snoozedUntil, device.snoozedUntil),
    dismissed: !!(account.dismissed || device.dismissed),
    peeked: account.peeked ?? device.peeked,
  }
}

/** What this device knows that the account doesn't, to send up so the user's other devices know it too. */
export function deviceOnlyMemory(
  account: SetupCallMemory,
  device: SetupCallMemory | undefined,
): Partial<SetupCallMemory> {
  const merged = mergeMemory(account, device)
  return {
    ...(merged.firstActionAt !== account.firstActionAt && { firstActionAt: merged.firstActionAt }),
    ...(merged.snoozedUntil !== account.snoozedUntil && { snoozedUntil: merged.snoozedUntil }),
    ...(merged.dismissed && !account.dismissed && { dismissed: true }),
    ...(merged.peeked !== account.peeked && { peeked: merged.peeked }),
  }
}

/** `first` is the invite's first showing; `later` is its return after Maybe later, which offers no later again. */
export type SetupCallMoment = 'first' | 'later'

/** Not a personal provider such as Gmail; no email doesn't count as professional. */
export function isProfessionalEmail(email: string | undefined): boolean {
  const domain = email?.toLowerCase().split('@')[1]
  return !!domain && !COMMON_PROVIDERS.has(domain)
}

export function isEligibleForSetupCall(
  user: { occupation?: string; email?: string },
  { requireProfessionalEmail = false } = {},
): boolean {
  if (!user.occupation || !PROFESSIONAL_ROLES.has(user.occupation)) return false
  if (user.email?.toLowerCase().endsWith('.edu')) return false
  return !requireProfessionalEmail || isProfessionalEmail(user.email)
}

/** `days` weekdays after `from`, at the same local time; Saturday and Sunday don't count. */
export function addBusinessDays(from: Date, days: number): Date {
  let date = from
  let left = days
  while (left > 0) {
    date = addDays(date, 1)
    if (date.getDay() !== 0 && date.getDay() !== 6) left--
  }
  return date
}

export function snoozeUntil(now: Date): number {
  return addBusinessDays(now, MAYBE_LATER_BUSINESS_DAYS).getTime()
}

/** Whether the invite is due: 24 hours after signup or the first meaningful action, whichever comes first. */
export function dueMoment(
  memory: SetupCallMemory | undefined,
  signedUpAt: number | undefined,
  now: number,
): SetupCallMoment | null {
  if (memory?.dismissed) return null
  if (memory?.snoozedUntil) return now >= memory.snoozedUntil ? 'later' : null
  if (memory?.firstActionAt || (signedUpAt !== undefined && now >= signedUpAt + DAY_MS)) return 'first'
  return null
}
